import { useEffect, useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { useAuth } from "../hooks/useAuth";
import {
  useGetCustomerMe,
  useGetMyCards,
  useCustomerStreaks,
  type CustomerStreak,
  type TenantCard,
} from "@orbit/api-client-react";
import { Wordmark } from "../components/Brand";
import { BottomNav } from "../components/BottomNav";
import { DeletionScheduledBanner } from "../components/DeletionScheduledBanner";
import { RecapCard } from "../components/RecapCard";
import { IdentityQrModal } from "../components/IdentityQrModal";
import { JoinQrScanner } from "../components/JoinQrScanner";
import { TodaysSurprisesSection } from "../components/TodaysSurprises";
import { formatJoinedAgo } from "../lib/relativeTime";
import {
  isPushSupported,
  requestPermissionAndSubscribe,
} from "../lib/push-notifications";

/**
 * Phase 18 — first-event push prompt key. Stored in localStorage; once
 * set the inline "Get notifications" banner stops appearing on Cards.
 * Distinct from the server-side `firstMeaningfulEventAt` because the
 * dismiss is a per-device decision (a customer may have already
 * dismissed on their phone but want it offered when they later visit
 * Orbit on a tablet).
 */
const PUSH_PROMPT_DISMISSED_KEY = "orbit-push-prompt-shown";

export function CardsPage() {
  const { isAuthenticated } = useAuth();
  const meQ = useGetCustomerMe({ enabled: isAuthenticated });
  const cardsQ = useGetMyCards({ enabled: isAuthenticated });
  // Phase 20 — streaks across all enrolled tenants. Server filters
  // to streak_enabled=true AND current_streak_count>=1 so we only
  // render badges where there is something to celebrate. Failures
  // are silent: the cards list still works without the streak chip.
  const streaksQ = useCustomerStreaks({ enabled: isAuthenticated });
  const streakByTenant = new Map<string, CustomerStreak>(
    (streaksQ.data?.streaks ?? []).map((s) => [s.tenantId, s]),
  );
  const [streakModal, setStreakModal] = useState<CustomerStreak | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  // D99 — persistent "Add another merchant" CTA below the cards
  // list. Previously the only entry to the in-app scanner from this
  // page lived inside `EmptyCardsState`, which disappeared the moment
  // the customer joined their first merchant — leaving them with no
  // discoverable way to add a second one without going back to the
  // Welcome page (which they're now redirected away from). Keeps its
  // own `scannerOpen` state separate from the EmptyCardsState
  // instance so each renders the modal independently.
  const [addMerchantScannerOpen, setAddMerchantScannerOpen] = useState(false);
  // Phase 18 — banner state. We start "showing" iff (a) the customer
  // has hit a meaningful event AND (b) they haven't dismissed before
  // on this device AND (c) push is actually supported here. Wrapped
  // in useEffect so the localStorage read doesn't run during SSR-ish
  // renders.
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushBanner, setPushBanner] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isPushSupported()) return;
    if (Notification.permission === "granted") return;
    if (Notification.permission === "denied") return;
    if (!meQ.data?.firstMeaningfulEventAt) return;
    if (localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY)) return;
    setShowPushPrompt(true);
  }, [isAuthenticated, meQ.data?.firstMeaningfulEventAt]);

  async function onEnablePush() {
    setPushBusy(true);
    const r = await requestPermissionAndSubscribe();
    setPushBusy(false);
    if (r.ok) {
      localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, "1");
      setShowPushPrompt(false);
      setPushBanner("Notifications enabled.");
      window.setTimeout(() => setPushBanner(null), 3500);
    } else if (r.errorCode === "IOS_NEEDS_INSTALL") {
      setPushBanner(
        "On iPhone: Share → Add to Home Screen, then open Orbit from the icon to enable.",
      );
    } else if (r.errorCode === "PERMISSION_DENIED") {
      localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, "1");
      setShowPushPrompt(false);
      setPushBanner(
        "Notifications blocked. Re-enable from your browser site settings.",
      );
    } else {
      setPushBanner(r.errorMessage ?? "Couldn't enable notifications.");
    }
  }

  function dismissPushPrompt() {
    localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, "1");
    setShowPushPrompt(false);
  }

  if (!isAuthenticated) return <Redirect to="/" />;

  const cards = cardsQ.data?.cards ?? [];
  const firstName = meQ.data?.name.split(" ")[0] ?? null;

  return (
    <div className="min-h-screen pb-28">
      <div className="mx-auto max-w-md px-6 pt-8">
        {/* Phase 12 — soft-deletion grace banner. Renders nothing when
            deletion isn't scheduled, so it doesn't push the header. */}
        <DeletionScheduledBanner />
        {/* Header: wordmark left, profile icon right → /profile */}
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link
            href="/profile"
            aria-label="Open profile"
            className="rounded-full p-2 -mr-2 text-navy hover:bg-navy/5"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              className="h-6 w-6"
              aria-hidden="true"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
            </svg>
          </Link>
        </header>

        {/* Greeting */}
        <section className="mt-6">
          {firstName ? (
            <h1 className="text-2xl font-bold text-navy">Hi {firstName} 👋</h1>
          ) : (
            <div className="h-7 w-40 animate-pulse rounded bg-navy/10" />
          )}
        </section>

        {/* THE primary action: full-width navy button above the fold */}
        <button
          type="button"
          onClick={() => setQrOpen(true)}
          className="mt-5 w-full rounded-2xl bg-navy py-4 text-lg font-semibold text-white shadow-lg shadow-navy/20 active:scale-[0.99] transition flex items-center justify-center gap-2.5"
          aria-label="Show my Orbit identity QR"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="h-6 w-6"
            aria-hidden="true"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <path d="M14 14h3v3h-3zM18 18h3v3h-3zM14 19h3v2" />
          </svg>
          Show My QR
        </button>

        {/* Phase 18 — first-event push prompt. Shown above the fold
            (under the QR CTA) once the customer has earned a real
            event AND hasn't already dismissed/granted. Exactly two
            actions: enable + not now. Inline status text replaces
            the banner once the user acts. */}
        {showPushPrompt && (
          <section className="mt-4 rounded-2xl border border-amber-light bg-amber-50/60 p-4">
            <div className="flex items-start gap-3">
              <div aria-hidden="true" className="text-2xl">🔔</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-navy">
                  Get notifications
                </p>
                <p className="mt-0.5 text-xs text-ink-dark">
                  Find out instantly when you earn rewards, get points, or have a new voucher waiting.
                </p>
                {pushBanner && (
                  <p className="mt-2 text-xs text-ink-dark/80">
                    {pushBanner}
                  </p>
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className="rounded-lg bg-navy px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                    onClick={onEnablePush}
                    disabled={pushBusy}
                  >
                    {pushBusy ? "Enabling…" : "Turn on"}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy/5"
                    onClick={dismissPushPrompt}
                    disabled={pushBusy}
                  >
                    Not now
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Phase 19 — real "Today's Surprises" list. Renders nothing
            when the customer has no enrolled tenants with enabled
            pools, so the placeholder Recap card stays as the sole
            row in that case. */}
        <TodaysSurprisesSection />

        {/* Phase 21 — real recap banner. Renders only when the customer
            has a recap generated within the last 7 days; otherwise the
            section collapses (no placeholder needed once a recap has
            shipped at least once for that customer). */}
        <section className="mt-5 grid grid-cols-1 gap-3">
          <RecapCard enabled={isAuthenticated} />
        </section>

        {/* Section header */}
        <section className="mt-8">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
            Your Cards
          </h2>

          <div className="mt-3">
            {cardsQ.isLoading ? (
              <CardSkeleton />
            ) : cardsQ.isError ? (
              <div className="card text-center">
                <p className="text-red-600 font-medium">
                  Couldn't load your cards.
                </p>
                <button
                  className="mt-2 text-navy underline text-sm"
                  onClick={() => cardsQ.refetch()}
                >
                  Try again
                </button>
              </div>
            ) : cards.length === 0 ? (
              <EmptyCardsState />
            ) : (
              <>
                <ul className="flex flex-col gap-3">
                  {cards.map((c) => (
                    <li key={c.tenantCustomerId}>
                      <Link
                        href={`/cards/${c.tenantCustomerId}`}
                        className="block hover:opacity-90 active:scale-[0.99] transition"
                      >
                        <CardItem
                          card={c}
                          streak={streakByTenant.get(c.tenantId) ?? null}
                          onStreakTap={setStreakModal}
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
                {/* D99 — persistent add-merchant CTA. Visible whenever
                    the customer already has at least one card so they
                    can keep adding more. Opens the same JoinQrScanner
                    used by EmptyCardsState; navigation uses the D98
                    full-page assign so iOS PWA can't render a blank
                    JoinPage behind the camera tear-down. */}
                <button
                  type="button"
                  onClick={() => setAddMerchantScannerOpen(true)}
                  className="mt-4 w-full rounded-2xl border border-dashed border-navy/30 bg-cream py-3 text-sm font-semibold text-navy hover:bg-amber-xlight active:scale-[0.99] transition flex items-center justify-center gap-2"
                  data-testid="button-add-another-merchant"
                  aria-label="Scan a Join QR to add another merchant"
                >
                  <span aria-hidden="true" className="text-base">＋</span>
                  Add another merchant
                </button>
              </>
            )}
          </div>
        </section>
      </div>

      {addMerchantScannerOpen && (
        <JoinQrScanner
          onClose={() => setAddMerchantScannerOpen(false)}
          onShortCode={(shortCode) => {
            // D99 — see the D98 notes in EmptyCardsState below; full-
            // page navigation so the JoinPage always renders cleanly
            // after the camera tears down (iOS PWA stalls otherwise).
            setAddMerchantScannerOpen(false);
            window.location.assign(`/j/${shortCode}`);
          }}
        />
      )}

      <IdentityQrModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        customerName={firstName}
      />

      <StreakDetailModal
        streak={streakModal}
        onClose={() => setStreakModal(null)}
      />

      <BottomNav />
    </div>
  );
}

function PlaceholderCard({
  emoji,
  title,
  subtitle,
}: {
  emoji: string;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="rounded-card border border-amber-light bg-amber-xlight px-4 py-3.5">
      <div className="text-xl" aria-hidden="true">{emoji}</div>
      <p className="mt-1 text-sm font-semibold text-navy">{title}</p>
      <p className="text-[11px] text-ink-dark">{subtitle}</p>
    </div>
  );
}

function CardItem({
  card,
  streak,
  onStreakTap,
}: {
  card: TenantCard;
  streak: CustomerStreak | null;
  onStreakTap: (s: CustomerStreak) => void;
}) {
  return (
    <article className="card">
      <div className="flex items-start gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-xlight text-2xl"
          aria-hidden="true"
        >
          {verticalEmoji(card.verticalOs)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-semibold text-navy truncate">
              {card.tenantName}
            </h3>
            <StatusBadge status={card.status} />
          </div>
          <div className="mt-1 flex items-center gap-2">
            <LoyaltyChip mode={card.loyaltyMode} />
            <span className="text-sm text-ink-dark truncate">
              {loyaltyValue(card)}
            </span>
          </div>
          {streak && streak.currentStreakCount > 0 ? (
            <div className="mt-2">
              <StreakBadge streak={streak} onTap={onStreakTap} />
            </div>
          ) : null}
          <p className="mt-2 text-xs text-ink-dark">{formatJoinedAgo(card.joinedAt)}</p>
        </div>
      </div>
    </article>
  );
}

/**
 * Phase 20 — streak chip rendered on each Card. Lives inside the
 * card's <Link> wrapper, so the button's onClick must call
 * `preventDefault` + `stopPropagation` to keep the surrounding link
 * navigation from firing when the customer just wants the cadence
 * explainer. At-risk renders amber w/ a "Visit by …" hint; healthy
 * streaks render the brand orange flame.
 */
function StreakBadge({
  streak,
  onTap,
}: {
  streak: CustomerStreak;
  onTap: (s: CustomerStreak) => void;
}) {
  const cadenceWord = cadenceShortLabel(streak.cadence);
  const base =
    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition active:scale-[0.97]";
  const styles = streak.atRisk
    ? "bg-amber-100 text-amber-900 ring-1 ring-amber-300"
    : "bg-orange-100 text-orange-800 ring-1 ring-orange-200";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onTap(streak);
      }}
      className={`${base} ${styles}`}
      aria-label={`${streak.currentStreakCount} ${cadenceWord} streak. Tap for details.`}
    >
      <span aria-hidden="true">{streak.atRisk ? "⏰" : "🔥"}</span>
      <span>
        {streak.currentStreakCount}-{cadenceWord} streak
      </span>
      {streak.atRisk ? (
        <span className="ml-1 text-amber-700">
          · Visit by {formatPeriodEnd(streak.periodEndsAt)}
        </span>
      ) : null}
    </button>
  );
}

function cadenceShortLabel(c: CustomerStreak["cadence"]): string {
  switch (c) {
    case "DAILY":
      return "day";
    case "WEEKLY":
      return "week";
    case "BIWEEKLY":
      return "fortnight";
    case "MONTHLY":
      return "month";
    default:
      return "period";
  }
}

function cadenceLongLabel(c: CustomerStreak["cadence"]): string {
  switch (c) {
    case "DAILY":
      return "every day";
    case "WEEKLY":
      return "every week (Mon–Sun, Manila time)";
    case "BIWEEKLY":
      return "every two weeks";
    case "MONTHLY":
      return "every month";
    default:
      return "each period";
  }
}

function formatPeriodEnd(iso: string): string {
  // Render in Asia/Manila — same TZ the server uses for period
  // boundaries (D74). The customer's device may be elsewhere when
  // travelling, but the streak window itself is tenant-local.
  try {
    return new Intl.DateTimeFormat("en-PH", {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "Asia/Manila",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatPeriodEndLong(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-PH", {
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "Asia/Manila",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function StreakDetailModal({
  streak,
  onClose,
}: {
  streak: CustomerStreak | null;
  onClose: () => void;
}) {
  if (!streak) return null;
  const startedAt = streak.streakStartedAt
    ? new Intl.DateTimeFormat("en-PH", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "Asia/Manila",
      }).format(new Date(streak.streakStartedAt))
    : "—";
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="streak-modal-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2
            id="streak-modal-title"
            className="text-base font-semibold text-navy"
          >
            {streak.tenantName} streak
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-2 py-1 text-sm text-ink-dark hover:bg-amber-xlight"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-3xl font-bold text-orange-600">
            {streak.currentStreakCount}
          </span>
          <span className="text-sm text-ink-dark">
            {cadenceShortLabel(streak.cadence)}
            {streak.currentStreakCount === 1 ? "" : "s"} in a row
          </span>
        </div>
        {streak.atRisk ? (
          <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
            ⏰ Visit by{" "}
            <strong>{formatPeriodEndLong(streak.periodEndsAt)}</strong> to keep
            it going.
          </p>
        ) : (
          <p className="mt-2 text-sm text-ink-dark">
            Next visit deadline:{" "}
            <strong>{formatPeriodEndLong(streak.periodEndsAt)}</strong>
          </p>
        )}
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-ink-dark">Longest streak</dt>
            <dd className="font-semibold text-navy">
              {streak.longestStreakCount}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-dark">Started</dt>
            <dd className="font-semibold text-navy">{startedAt}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-dark">Cadence</dt>
            <dd className="font-semibold text-navy">
              {cadenceLongLabel(streak.cadence)}
            </dd>
          </div>
        </dl>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 w-full rounded-lg bg-navy py-2.5 text-sm font-semibold text-white"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function LoyaltyChip({ mode }: { mode: TenantCard["loyaltyMode"] }) {
  const label = mode === "POINTS" || mode === "STAMPS" ? mode : "POINTS";
  return (
    <span className="inline-flex items-center rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-navy">
      {label}
    </span>
  );
}

function loyaltyValue(card: TenantCard): string {
  // Phase 5 wires real balances into tenant_customers — points/stamps
  // tick up on every successful POST /tenant/scan, so we render the
  // live values straight off the card. The denominator (`/ N stamps`)
  // is the per-merchant reward target carried on `stampsRewardThreshold`
  // (defaults to 10 server-side; merchants can configure 5 for a coffee
  // programme, 12 for a carwash, etc.).
  const threshold = card.stampsRewardThreshold;
  switch (card.loyaltyMode) {
    case "STAMPS":
      return `${card.stampsCount} / ${threshold} stamps`;
    case "POINTS":
      return `${card.pointsBalance.toFixed(2)} points`;
    case "VISIT_TIERS":
      return `${card.totalVisits} visits`;
    case "HYBRID":
      return `${card.pointsBalance.toFixed(2)} points · ${card.stampsCount} / ${threshold} stamps`;
    default:
      return `${card.pointsBalance.toFixed(2)} points`;
  }
}

function verticalEmoji(v: TenantCard["verticalOs"]): string {
  // Map each vertical OS to a representative emoji. The full set of
  // verticalOs values lives in lib/api-zod (VerticalOsSchema). Any
  // unknown future addition falls through to the storefront emoji.
  switch (v) {
    case "BARESCA":
      return "☕";
    case "CHEFANTHONY":
      return "🍝";
    case "CARWASH":
      return "🚿";
    case "WATERSTATION":
      return "💧";
    case "HAOS":
      return "🏠";
    case "LEXOS":
      return "⚖️";
    case "AIRCONSERV":
      return "❄️";
    case "PACKDIST":
      return "📦";
    case "CLINICBEAUTY":
      return "💆";
    case "STANDALONE":
      return "🏪";
    default:
      return "🏪";
  }
}

function StatusBadge({ status }: { status: TenantCard["status"] }) {
  if (status === "ACTIVE") return null;
  const styles: Record<TenantCard["status"], string> = {
    ACTIVE: "",
    PAUSED: "bg-yellow-100 text-yellow-800",
    CHURNED: "bg-ink-light text-ink-dark",
    BANNED: "bg-red-100 text-red-800",
    // Phase 12 — REVOKED rows are filtered out of /cards by the
    // backend, but the badge style is still required by the TS
    // exhaustive Record<status, string>.
    REVOKED: "bg-ink-light text-ink-dark",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${styles[status]}`}
    >
      {status.toLowerCase()}
    </span>
  );
}

function CardSkeleton() {
  return (
    <ul className="flex flex-col gap-3">
      {[0, 1].map((i) => (
        <li key={i} className="card">
          <div className="flex gap-3">
            <div className="h-12 w-12 shrink-0 animate-pulse rounded-full bg-amber-xlight" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-2/3 animate-pulse rounded bg-navy/10" />
              <div className="h-3 w-1/3 animate-pulse rounded bg-navy/5" />
              <div className="h-3 w-1/4 animate-pulse rounded bg-navy/5" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyCardsState() {
  const [, navigate] = useLocation();
  const [scannerOpen, setScannerOpen] = useState(false);

  return (
    <>
      <div className="card text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-amber-light/60 flex items-center justify-center text-2xl">
          💳
        </div>
        <h3 className="mt-3 text-base font-semibold text-navy">No cards yet</h3>
        <p className="mt-1 text-sm text-ink-dark">
          Scan any M42 merchant&apos;s Join QR to add your first card.
        </p>
        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="btn-primary mt-4 w-full"
          data-testid="button-open-scanner-empty"
        >
          Open camera to scan
        </button>
      </div>

      {scannerOpen && (
        <JoinQrScanner
          onClose={() => setScannerOpen(false)}
          onShortCode={(shortCode) => {
            // D98 — Close the scanner, then do a FULL page navigation
            // (window.location.assign) instead of a wouter SPA route
            // change. The earlier D90 rAF-defer + client-side navigate
            // worked on most browsers but on iOS PWA the React commit
            // could still stall behind the still-tearing-down camera
            // MediaStream — leaving JoinPage blank until manual
            // refresh. A full navigation matches the native-camera
            // scan path exactly (SW serves index.html → SPA boots
            // fresh → routes to /j/<slug>), guaranteeing a clean
            // React tree. The ~200ms reload is imperceptible after a
            // QR scan transition.
            setScannerOpen(false);
            window.location.assign(`/j/${shortCode}`);
          }}
        />
      )}
    </>
  );
}
